import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ReactNode } from 'react';
import { useIntl } from 'react-intl';

import {
  apiDeleteStoryQuestionsCategoryById,
  StoryQuestionsCategory,
} from '@/shared/api';
import { storyByIdQueryOptions } from '@/shared/queries';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shared/ui/dialog';

type DeleteStorySectionDialogProps = {
  children: ReactNode;
  sectionId: string;
  storyId: string;
};

export const DeleteStorySectionDialog = ({
  children,
  sectionId,
  storyId,
}: DeleteStorySectionDialogProps) => {
  const intl = useIntl();
  const queryClient = useQueryClient();

  const deleteStorySectionMutation = useMutation<StoryQuestionsCategory, Error, string>({
    mutationFn: (id) => {
      return apiDeleteStoryQuestionsCategoryById(String(id)).then(({ data }) => data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: storyByIdQueryOptions(storyId).queryKey,
      });
    },
  });

  return (
    <Dialog>
      <DialogTrigger asChild>{children}</DialogTrigger>

      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{intl.formatMessage({ id: 'dialog.confirmAction' })}</DialogTitle>
        </DialogHeader>

        <DialogFooter>
          <Button
            variant="destructive"
            onClick={() => deleteStorySectionMutation.mutate(sectionId)}
          >
            {intl.formatMessage({ id: 'button.delete' })}
          </Button>

          <DialogClose asChild>
            <Button variant="secondary">
              {intl.formatMessage({ id: 'button.close' })}
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
